import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const textureMaskedTypeSkill: CodeoSkill = {
    name: SKILL_NAMES.textureMaskedType,
    kind: 'codeo',
    description:
        'Cut a word out of a moving texture so the type becomes a window onto something behind it ' +
        '— for a title that should carry material rather than colour.',
    documentUsesSkill: check(
        (script, markup) =>
            /background-clip:\s*text|backgroundClip/.test(markup) &&
            /backgroundPosition|background-position/.test(script),
    ),
    instructions: `# Texture Masked Type

The letters are not painted; they are a hole. Behind them a texture drifts — a gradient field, a pattern, a grain — and it shows only inside the type. Because the material moves and the word does not, the headline stays perfectly readable while never being static.

It solves the most common failure in a title card: the type lands and the frame dies. Here the type lands and the *inside* of it keeps moving.

## Markup

The mask is the text; the moving layer sits behind it and is clipped to it. \`background-clip: text\` is the cheapest version and needs the text colour to be transparent.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="masked-title" class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 220px; font-weight: 900; letter-spacing: -0.02em; line-height: 1; color: transparent; -webkit-background-clip: text; background-clip: text; background-image: linear-gradient(115deg, var(--l5-color-2, #38bdf8) 0%, var(--l5-color-1, #101820) 45%, var(--l5-color-2, #38bdf8) 100%); background-size: 300% 100%; background-position: 0% 0%;">MOMENTUM</div>
</div>
\`\`\`

## Timeline code

Move the *background*, not the element. The word stays exactly where it is while the material travels through it.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#masked-title', { opacity: 0, backgroundPosition: '0% 0%' });

tl.to('#masked-title', { opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.2);
tl.to('#masked-title', {
    backgroundPosition: '100% 0%',
    duration: 5.2,
    ease: 'none',
}, 0.2);

window.__timelines = { main: tl };
\`\`\`

\`ease: 'none'\` over a long duration is what makes the material read as continuously present rather than as an effect that fires and stops.

## Adapt

- **Material**: a two-stop gradient reads as a sheen; a three-stop palette gradient reads as a print; a repeating conic or striped gradient reads as a pattern and is much louder.
- **Speed**: the background should cross in 4–6s. Faster reads as a shimmer effect; slower and it appears not to move at all.
- **Photographic fill**: an image behind the mask makes the word a window onto a scene. Powerful, and only worth it when the image means something.
- **Grain inside the type**: layering a subtle noise over the gradient gives the letters material weight — see \`${SKILL_NAMES.grainAndBloom}\`.
- **Entrance**: pair with a tracking close or a per-word reveal for the arrival, and let the texture run underneath both — see \`${SKILL_NAMES.letterSpacingSettle}\`.
- **Knockout instead**: inverting it — solid type punched out of a filled block — is the same idea with the opposite figure/ground, and reads heavier.

## Pitfalls

- The text colour must be \`transparent\` or the fill paints over the mask and nothing shows through.
- Set both \`-webkit-background-clip\` and \`background-clip\`; one alone silently no-ops in one renderer.
- \`background-size\` must exceed 100% on the axis being moved, or there is nothing to travel and \`backgroundPosition\` does nothing visible.
- The style guidance calls out gradient text as a generic default. This technique earns it only when the material is *moving* and is part of the idea — a static gradient headline is exactly the tell it warns about.
- Keep the word short and the weight heavy. Thin letterforms give the texture no room to be seen and the effect vanishes.
- Contrast is now variable across the word: check the *lowest*-contrast moment of the sweep, not just the first frame.
- One masked headline per video. Two makes it a template.`,
};
